import { Section } from "@/components/ds/section";
import { btn } from "@/components/ds/styles";
import { createFileRoute, Link } from "@tanstack/react-router";
import { formatUsdShort } from "@/lib/payments/currency";
import { MarkLast } from "@/components/public/reading-page";
import { BookOpen } from "lucide-react";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useTraumaPurchaseState } from "@/lib/trauma/purchase-query";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { TraumaPreviewBanner } from "@/components/trauma/preview-banner";
import { loadTraumaCatalogOrRedirect, traumaCatalogQuery } from "@/lib/trauma/catalog-query";
import { TRAUMA_UI, TRAUMA_BUY_UI as BUY } from "@/lib/trauma/ui-copy";
import { canonicalLink, canonicalMeta, ogImageMeta } from "@/lib/seo";

/** «صدمات الطفولة» catalog — hidden (302 home) until trauma_enabled, except for admins. */
export const Route = createFileRoute("/_public/courses/")({
  loader: ({ context }) => loadTraumaCatalogOrRedirect(context.queryClient),
  head: ({ loaderData }) => {
    const description = loaderData?.series.intro.replace(/\s+/g, " ").slice(0, 155) ?? "";
    return {
      meta: [
        { title: TRAUMA_UI.catalogTitle },
        { name: "description", content: description },
        { property: "og:title", content: TRAUMA_UI.catalogTitle },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
        { property: "og:locale", content: "ar_SY" },
        ...(loaderData?.enabled ? [] : [{ name: "robots", content: "noindex, nofollow" }]),
        ...ogImageMeta(),
        ...canonicalMeta("/courses"),
      ],
      links: [canonicalLink("/courses")],
    };
  },
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: CoursesCatalog,
});

const softBtn = "inline-flex min-h-12 shrink-0 items-center justify-center rounded-ds-pill bg-amber-100 px-5 font-display text-[15px] font-bold text-brown-700 transition-colors duration-ds-fast hover:bg-amber-200";
const primaryBtn = `${btn.primary}`;

function CoursesCatalog() {
  const initialData = Route.useLoaderData();
  const queryClient = useQueryClient();
  const query = traumaCatalogQuery();
  if (queryClient.getQueryData(query.queryKey) === undefined) queryClient.setQueryData(query.queryKey, initialData);
  const { data } = useSuspenseQuery(traumaCatalogQuery());
  const { offers, owned } = useTraumaPurchaseState();
  const bundle = offers?.["trauma-bundle"];
  if (!data) return null;
  const bundlePrice = data.prices["trauma-bundle"];
  return (
    <div>
      <Section tone="white" edge="hairline" inner="mx-auto max-w-3xl px-5 pb-10 pt-10 lg:pb-14 lg:pt-16">
        <TraumaPreviewBanner show={!data.enabled} />
        <p className="font-display text-[14px] font-bold text-amber-800">الدورات</p>
        <h1 className="mt-3 font-display text-[34px] font-extrabold leading-[1.3] text-text-primary lg:text-[42px]"><MarkLast text={data.series.title} /></h1>
        <p className="mt-4 whitespace-pre-line text-[19px] leading-[1.8] text-brown-500">{data.series.intro}</p>
      </Section>

      <Section tone="cream" className="pb-16 pt-2">
      {data.courses.length === 0 ? (
        <div className="mx-auto mt-10 max-w-3xl px-5">
          <Link to="/book" className="flex min-h-14 items-center border-y border-hairline-subtle py-4 font-display text-[17px] font-bold text-brown-700 hover:text-amber-800">
            الدورات قريباً — حجز استشارة ←
          </Link>
        </div>
      ) : (
        <>
          {bundlePrice != null && (
            <section className="mx-3 mt-10 rounded-ds-2xl bg-amber-100 px-6 py-9 lg:mx-auto lg:max-w-[76rem] lg:px-12">
              <div className="mx-auto max-w-3xl">
                <h2 className="font-display text-[23px] font-extrabold text-text-primary">{TRAUMA_UI.bundleTitle}</h2>
                <p className="mt-3 font-display text-[40px] font-extrabold leading-none text-amber-800"><span dir="ltr">{formatUsdShort(bundlePrice)}</span></p>
                {bundle?.canBuy ? (
                  <Link to="/courses/trauma/buy/$productSlug" params={{ productSlug: "trauma-bundle" }} className={`${primaryBtn} mt-6`}>{BUY.buyBundle} ←</Link>
                ) : null}
              </div>
            </section>
          )}

          <ol className="mx-auto mt-8 max-w-3xl divide-y divide-hairline-subtle px-5">
            {data.courses.map((c) => {
              const isOwned = owned.includes(c.id);
              const price = data.prices[`trauma-${c.id}`];
              return (
                <li key={c.id} className="flex flex-wrap items-start gap-4 py-6">
                  <span className="grid size-[52px] shrink-0 place-items-center rounded-ds-md bg-amber-100 text-amber-800" aria-hidden>
                    <BookOpen className="size-6" strokeWidth={1.75} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h2 className="font-display text-[19px] font-extrabold leading-snug text-text-primary">{c.title}</h2>
                    <p className="mt-1 line-clamp-1 text-[14px] text-brown-500">{c.subtitle}</p>
                    <p className="mt-1 text-[14px] text-brown-500">{TRAUMA_UI.facts}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      {isOwned ? (
                        <span className="rounded-ds-pill bg-green-100 px-3 py-1 font-display text-[13px] font-bold text-green-800">{BUY.ownedBadge}</span>
                      ) : price != null ? (
                        <span dir="ltr" className="font-display text-[23px] font-extrabold text-amber-800">{formatUsdShort(price)}</span>
                      ) : null}
                      <Link to="/courses/trauma/$id" params={{ id: c.id }} className={`${softBtn} ms-auto`}>
                        {isOwned ? "فتح الدورة ←" : "التفاصيل ←"}
                      </Link>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </>
      )}
      </Section>
    </div>
  );
}
